// ==========================
// Version 10: pages/LandingPage.js
// - Original landing page, preserved at "/landingpage-3"
// - Promise-led hero + 4 use-cases + trust + CTA
// - Uses locked brand palette + minimal inline styling
// - Positions immortalisation as the central product value
// - Adds ReactBits Galaxy, SplitText, and SpotlightCard effects
// - Makes the first viewport a complete framed hero page
// - Makes the Galaxy hero full-bleed and moves landing nav into the first viewport
// ==========================
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BrandLogo from '../components/BrandLogo';
import Galaxy from '../components/Galaxy';
import Seo from '../components/Seo';

const NAVY = '#0A1A33';
const GOLD = '#C9A44C';
const IVORY = '#FDFCF9';

function LandingPage({ pagePath = "/landingpage-3" }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const start = () => {
    if (currentUser) navigate('/home');
    else navigate('/register');
  };

  const pageUrl =
    typeof window !== 'undefined' ? `${window.location.origin}${pagePath}` : undefined;

  return (
    <div style={styles.page}>
      <LandingMotionStyles />

      <Seo
        title="Blessed with a Kiss | Immortalise what matters"
        description="Preserve a meaningful memory with its story, photos, date and place, then immortalise it as verifiable digital proof."
        url={pageUrl}
        type="website"
      />

      {/* HERO */}
      <section style={styles.hero} className="bwak-landing-hero">
        <div style={styles.heroFrame}>
          <div style={styles.galaxyLayer} aria-hidden="true">
            <Galaxy
              mouseRepulsion
              mouseInteraction
              density={1.3}
              glowIntensity={0.5}
              saturation={0.4}
              hueShift={240}
              twinkleIntensity={0.5}
              starSpeed={0.7}
              transparent
            />
          </div>

          <LandingHeroNav currentUser={currentUser} />

          <div style={styles.heroCenter}>
            <div style={styles.kicker}>A place for the moments that matter</div>

            <SplitText text="Some things deserve to last." />

            <p style={styles.lead}>
              Preserve a meaningful moment with its story, photos, date and place.
              <br />
              {' '}
              Then immortalise it as verifiable digital proof that is yours to keep.
            </p>

            <div style={styles.heroCtaRow} className="bwak-hero-cta-row">
              <button
                type="button"
                onClick={start}
                style={styles.heroPrimaryBtn}
                className="bwak-star-button"
              >
                {currentUser ? 'Immortalise a memory' : 'Create your first memory'}
              </button>

              <Link to="/map" style={styles.heroSecondaryLink}>
                Explore the global map →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section style={styles.visualSection}>
        <div style={styles.container}>
          <div style={styles.visualGrid} className="bwak-visual-grid">
            <div style={styles.visualCopy} className="bwak-scroll-reveal">
              <div style={styles.kicker}>How it works</div>
              <h2 style={styles.h2}>From a moment to a memory that lasts.</h2>
              <p style={styles.p}>
                BWK transforms the moments that matter into lasting, verifiable keepsakes.
              </p>
            </div>
            <HeroProofVisual />
          </div>
        </div>
      </section>

      {/* FOUR STEPS */}
      <section style={styles.sectionAlt}>
        <div style={styles.container}>
          <h2 style={styles.h2}>Your memory. Preserved in four steps.</h2>

          <div style={styles.cards} className="bwak-promise-grid">
            <Card
              number="1"
              title="Tell its story"
              q="Capture what made the moment meaningful."
              a="Add the words, photos and date that make this memory yours."
            />
            <Card
              number="2"
              title="Place it on the map"
              q="Connect the memory to where it happened."
              a="Give the moment a place while choosing how much location information others can see."
            />
            <Card
              number="3"
              title="Prepare it for permanence"
              q="Review the memory you want to preserve."
              a="Choose what will become part of its lasting record and confirm every detail."
            />
            <Card
              number="4"
              title="Immortalise it"
              q="Turn your memory into verifiable digital proof."
              a="Mint a unique keepsake that records your story and belongs to you."
            />
          </div>

          <div style={styles.centerCta}>
            <button
              type="button"
              onClick={start}
              style={styles.primaryBtn}
              className="bwak-star-button"
            >
              {currentUser ? 'Immortalise a memory' : 'Begin your memory'}
            </button>
          </div>
        </div>
      </section>

      {/* DIGITAL OWNERSHIP */}
      <section style={styles.section}>
        <div style={styles.container}>
          <div style={styles.trustBox} className="bwak-scroll-reveal">
            <div style={styles.kicker}>Your memory. Your proof.</div>
            <h2 style={styles.assetHeading}>
              More than something you save. Something you own.
            </h2>
            <p style={styles.p}>
              When you immortalise a memory, BWK transforms it into a unique digital asset
              on the blockchain. It becomes connected to your crypto wallet, giving you
              verifiable proof that the memory belongs to you.
            </p>
            <div style={styles.trustGrid} className="bwak-trust-grid">
              <TrustItem
                title="Unique"
                text="Each immortalised memory has its own digital identity, created from the story, photos, date and details you choose to preserve."
              />
              <TrustItem
                title="Owned by you"
                text="The digital asset is held in your crypto wallet, your personal digital vault for the memories that form part of your legacy."
              />
              <TrustItem
                title="Verifiable"
                text="The blockchain records when the memory was immortalised and who owns it. This proof can be independently checked."
              />
            </div>

            <p style={styles.assetStatement}>
              A photo can be copied. A post can disappear. An immortalised BWK memory
              carries its own proof of origin, permanence and ownership.
            </p>

            <div style={styles.centerCta}>
              <button
                type="button"
                onClick={start}
                style={styles.primaryBtn}
                className="bwak-star-button"
              >
                Immortalise your memory
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FUTURE VALUE */}
      <section style={styles.futureSection}>
        <div style={styles.container}>
          <div style={styles.futureBox} className="bwak-scroll-reveal">
            <div style={styles.futureKicker}>The value of what is real</div>
            <h2 style={styles.futureHeading}>
              In a world of generated content, your real story becomes more valuable.
            </h2>
            <div style={styles.futureCopy}>
              <p>
                As AI makes it easier to create convincing images, stories and identities,
                authentic human memories will become harder to recognise.
              </p>
              <p>
                BWK gives everyone the ability to preserve their life stories, prove where
                they came from and establish ownership of what is truly theirs.
              </p>
            </div>
            <p style={styles.futureStatement}>
              The future will contain unlimited content. Proven human stories will remain scarce.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={styles.final}>
        <div style={styles.container}>
          <div style={styles.finalBox}>
            <h2 style={styles.h2}>A moment mattered to you.</h2>
            <p style={styles.p}>Keep it.</p>

            <div style={styles.ctaRow}>
              <button
                type="button"
                onClick={start}
                style={styles.primaryBtn}
                className="bwak-star-button"
              >
                {currentUser ? 'Go to Home' : 'Start a memory'}
              </button>
              {!currentUser && (
                <Link to="/login" style={styles.secondaryLink}>
                  I already have an account →
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function LandingHeroNav({ currentUser }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const links = currentUser
    ? [
        { to: '/home', label: 'Home' },
        { to: '/map', label: 'Global Map' },
        { to: '/profile', label: 'Profile' },
      ]
    : [
        { to: '/map', label: 'Global Map' },
        { to: '/login', label: 'Login' },
        { to: '/register', label: 'Register', primary: true },
      ];

  return (
    <div style={styles.heroNav} className="bwak-hero-nav">
      <Link to="/" style={styles.heroBrand} aria-label="Blessed with a Kiss home">
        <BrandLogo size={46} decorative style={styles.heroBrandMark} />
        <span style={styles.heroBrandText}>Blessed With A Kiss</span>
      </Link>

      <nav style={styles.heroNavLinks} aria-label="Landing">
        {links.map((link) => (
          <Link
            key={link.to}
            to={link.to}
            style={link.primary ? styles.heroNavPrimary : styles.heroNavLink}
          >
            {link.label}
          </Link>
        ))}
      </nav>

      <button
        type="button"
        style={styles.heroMenuButton}
        className="bwak-hero-menu-button"
        aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={menuOpen}
        aria-controls="bwak-hero-mobile-menu"
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span className="bwak-menu-line" />
        <span className="bwak-menu-line" />
        <span className="bwak-menu-line" />
      </button>

      {menuOpen ? (
        <nav
          id="bwak-hero-mobile-menu"
          style={styles.heroMobileMenu}
          className="bwak-hero-mobile-menu"
          aria-label="Mobile landing"
        >
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              style={link.primary ? styles.heroMobileMenuPrimary : styles.heroMobileMenuLink}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      ) : null}
    </div>
  );
}

function Card({ number, title, q, a }) {
  const ref = React.useRef(null);

  const onPointerMove = (event) => {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    node.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
    node.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
  };

  return (
    <div
      ref={ref}
      style={styles.card}
      className="bwak-spotlight-card"
      onPointerMove={onPointerMove}
    >
      <div style={styles.cardNumber}>Step {number}</div>
      <div style={styles.cardTitle}>{title}</div>
      <div style={styles.q}>{q}</div>
      <div style={styles.a}>{a}</div>
    </div>
  );
}

function SplitText({ text }) {
  const words = text.split(' ');

  return (
    <h1 style={styles.h1} aria-label={text}>
      {words.map((word, index) => (
        <React.Fragment key={`${word}-${index}`}>
          <span
            aria-hidden="true"
            className="bwak-split-word"
            style={{ '--word-index': index }}
          >
            {word}
          </span>
          {index < words.length - 1 ? ' ' : null}
        </React.Fragment>
      ))}
    </h1>
  );
}

function HeroProofVisual() {
  return (
    <article style={styles.heroCard} className="bwak-proof-visual">
      <div style={styles.heroCardTop}>
        <BrandLogo size={38} decorative style={styles.badge} />
        <div>
          <span style={styles.badgeText}>Immortalised memory</span>
          <div style={styles.proofStatus}>Verified ownership</div>
        </div>
      </div>

      <img
        src="/images/first-kiss-memory.jpg"
        alt="A fictional couple sharing their first kiss beside the River Thames at dusk"
        style={styles.proofPhoto}
      />

      <div style={styles.proofContent}>
        <div style={styles.proofEyebrow}>Our first kiss</div>
        <h3 style={styles.proofTitle}>The Night We Knew</h3>
        <div style={styles.proofMeta}>
          <span>12 October 2024</span>
          <span>London, England</span>
        </div>

        <p style={styles.proofStory}>
          We stopped beside the river after walking for hours. The city carried on around
          us, but for one quiet moment, it felt as though the whole night belonged to us.
        </p>

        <div style={styles.proofLedger} aria-label="Blockchain ownership details">
          <div>
            <span style={styles.proofLabel}>Owner</span>
            <strong style={styles.proofValue}>0x7A3F...91C2</strong>
          </div>
          <div>
            <span style={styles.proofLabel}>Network</span>
            <strong style={styles.proofValue}>Ethereum</strong>
          </div>
          <div>
            <span style={styles.proofLabel}>Memory proof</span>
            <strong style={styles.proofValue}>#BWK-0001</strong>
          </div>
        </div>
      </div>
    </article>
  );
}

function TrustItem({ title, text }) {
  return (
    <div style={styles.trustItem}>
      <div style={styles.trustTitle}>{title}</div>
      <div style={styles.trustText}>{text}</div>
    </div>
  );
}

function LandingMotionStyles() {
  return (
    <style>
      {`
        .bwak-split-word {
          display: inline-block;
          opacity: 0;
          transform: translateY(24px);
          animation: bwak-split-rise 720ms cubic-bezier(0.2, 0.75, 0.25, 1) forwards;
          animation-delay: calc(var(--word-index) * 110ms);
          will-change: opacity, transform;
        }

        .bwak-split-word:not(:last-child) {
          margin-right: 0.18em;
        }

        .bwak-star-button {
          position: relative;
          overflow: hidden;
          isolation: isolate;
        }

        .bwak-star-button::before {
          content: "";
          position: absolute;
          inset: -2px;
          background: linear-gradient(
            115deg,
            transparent,
            rgba(253,252,249,0.70),
            transparent
          );
          transform: translateX(-120%);
          animation: bwak-button-shine 4.8s ease-in-out infinite;
          pointer-events: none;
        }

        .bwak-proof-visual {
          position: relative;
          transform: perspective(900px) rotateY(-5deg) rotateX(3deg);
          animation: bwak-float-card 6s ease-in-out infinite;
        }

        .bwak-proof-map {
          position: absolute;
          inset: 14px;
          border-radius: 18px;
          overflow: hidden;
          opacity: 0.62;
          background:
            radial-gradient(circle at 20% 32%, rgba(201,164,76,0.36), transparent 7%),
            radial-gradient(circle at 76% 24%, rgba(201,164,76,0.24), transparent 8%),
            radial-gradient(circle at 54% 72%, rgba(253,252,249,0.12), transparent 9%),
            linear-gradient(135deg, rgba(253,252,249,0.06), rgba(10,26,51,0.08));
        }

        .bwak-proof-line {
          position: absolute;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(201,164,76,0.72), transparent);
          transform-origin: left center;
        }

        .bwak-proof-line-one { left: 24%; top: 34%; width: 42%; transform: rotate(-10deg); }
        .bwak-proof-line-two { left: 34%; top: 55%; width: 46%; transform: rotate(20deg); }
        .bwak-proof-line-three { left: 16%; top: 70%; width: 58%; transform: rotate(-24deg); }

        .bwak-memory-pin {
          position: absolute;
          width: 12px;
          height: 12px;
          border-radius: 999px;
          background: #C9A44C;
          box-shadow: 0 0 0 8px rgba(201,164,76,0.14), 0 0 28px rgba(201,164,76,0.55);
          animation: bwak-pin-pulse 2.6s ease-in-out infinite;
        }

        .bwak-memory-pin-one { left: 21%; top: 29%; }
        .bwak-memory-pin-two { right: 20%; top: 22%; animation-delay: -0.7s; }
        .bwak-memory-pin-three { left: 52%; bottom: 23%; animation-delay: -1.4s; }

        .bwak-spotlight-card {
          position: relative;
          overflow: hidden;
          --spot-x: 50%;
          --spot-y: 50%;
          transition: transform 220ms ease, border-color 220ms ease, box-shadow 220ms ease;
        }

        .bwak-spotlight-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(
            circle at var(--spot-x) var(--spot-y),
            rgba(201,164,76,0.26),
            transparent 36%
          );
          opacity: 0;
          transition: opacity 180ms ease;
          pointer-events: none;
        }

        .bwak-spotlight-card:hover {
          transform: translateY(-4px);
          border-color: rgba(201,164,76,0.70);
          box-shadow: 0 24px 70px rgba(10,26,51,0.36);
        }

        .bwak-spotlight-card:hover::before {
          opacity: 1;
        }

        .bwak-scroll-reveal {
          animation: bwak-soft-in 720ms ease both;
          animation-timeline: view();
          animation-range: entry 0% cover 30%;
        }

        @keyframes bwak-split-rise {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bwak-button-shine {
          0%, 58% { transform: translateX(-120%); opacity: 0; }
          68% { opacity: 0.52; }
          82%, 100% { transform: translateX(120%); opacity: 0; }
        }

        @keyframes bwak-float-card {
          0%, 100% { translate: 0 0; }
          50% { translate: 0 -8px; }
        }

        @keyframes bwak-pin-pulse {
          0%, 100% { transform: scale(1); opacity: 0.82; }
          50% { transform: scale(1.18); opacity: 1; }
        }

        @keyframes bwak-soft-in {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 920px) {
          .bwak-visual-grid {
            grid-template-columns: 1fr !important;
          }

          .bwak-proof-visual {
            transform: none;
            max-width: 520px;
          }

          .bwak-promise-grid {
            grid-template-columns: 1fr !important;
          }

          .bwak-trust-grid {
            grid-template-columns: 1fr !important;
          }
        }

	        @media (max-width: 620px) {
	          .bwak-hero-nav {
	            align-items: center !important;
	            gap: 12px !important;
	            padding: 12px 14px !important;
	          }

            .bwak-hero-nav > nav[aria-label="Landing"] {
              display: none !important;
            }

            .bwak-hero-menu-button {
              display: inline-grid !important;
              grid-template-rows: repeat(3, 2px) !important;
              align-content: center !important;
              justify-content: center !important;
            }

            .bwak-hero-mobile-menu {
              display: grid !important;
            }

            .bwak-menu-line {
              display: block;
              width: 18px;
              height: 2px;
              border-radius: 999px;
              background: #C9A44C;
            }

            .bwak-hero-nav [aria-label="Blessed with a Kiss home"] {
              gap: 9px !important;
              letter-spacing: 1.6px !important;
            }

            .bwak-hero-nav [aria-label="Blessed with a Kiss home"] > span:first-child {
              width: 36px !important;
              height: 36px !important;
              font-size: 10px !important;
            }

            .bwak-hero-nav [aria-label="Blessed with a Kiss home"] > span:last-child {
              font-size: 11px !important;
              max-width: 172px !important;
              overflow: hidden !important;
              text-overflow: ellipsis !important;
            }

            .bwak-landing-hero h1 {
              font-size: clamp(38px, 11vw, 48px) !important;
              line-height: 1.02 !important;
              max-width: calc(100vw - 36px) !important;
            }

            .bwak-landing-hero p {
              font-size: 17px !important;
              max-width: calc(100vw - 36px) !important;
            }

            .bwak-landing-hero p br {
              display: none !important;
            }

            .bwak-landing-hero a,
            .bwak-landing-hero button {
              max-width: 100% !important;
            }

            .bwak-landing-hero .bwak-hero-cta-row {
              width: min(100%, 306px) !important;
              display: grid !important;
              grid-template-columns: 1fr !important;
              gap: 12px !important;
            }
	        }

	        @media (max-width: 520px) {
	          .bwak-hero-nav {
	            padding: 10px 12px !important;
	          }

	          .bwak-landing-hero {
            min-height: auto !important;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .bwak-split-word,
          .bwak-star-button::before,
          .bwak-proof-visual,
          .bwak-memory-pin,
          .bwak-scroll-reveal {
            animation: none !important;
          }

          .bwak-split-word {
            opacity: 1;
            transform: none;
          }
        }
      `}
    </style>
  );
}

const styles = {
  page: {
    minHeight: '100dvh',
    background: NAVY,
    color: IVORY,
  },
  container: {
    maxWidth: 1120,
    margin: '0 auto',
    padding: '0 16px',
  },

  hero: {
    position: 'relative',
    minHeight: '100dvh',
    padding: 0,
    overflow: 'hidden',
    display: 'grid',
    placeItems: 'stretch',
  },

  heroFrame: {
    position: 'relative',
    overflow: 'hidden',
    minHeight: '100dvh',
    width: '100%',
    background: 'linear-gradient(180deg, rgba(5,7,18,0.94), rgba(10,26,51,0.92))',
    display: 'grid',
    placeItems: 'center',
    padding: 'clamp(102px, 14vh, 150px) 18px 64px',
  },

  galaxyLayer: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
  },

  heroNav: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 18,
    padding: '24px clamp(18px, 4vw, 56px)',
  },

  heroBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: IVORY,
    textDecoration: 'none',
    fontWeight: 900,
    letterSpacing: 2.8,
    textTransform: 'uppercase',
  },

  heroBrandMark: {
    width: 46,
    height: 46,
    borderRadius: 999,
    padding: 3,
    background: 'rgba(10,26,51,0.58)',
    border: '1px solid rgba(201,164,76,0.35)',
    boxShadow: '0 18px 38px rgba(0,0,0,0.28), 0 0 0 1px rgba(253,252,249,0.10)',
  },

  heroBrandText: {
    fontSize: 'clamp(12px, 1.3vw, 15px)',
    whiteSpace: 'nowrap',
  },

  heroNavLinks: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 'clamp(10px, 2vw, 24px)',
    flexWrap: 'wrap',
  },

  heroNavLink: {
    color: 'rgba(253,252,249,0.92)',
    textDecoration: 'none',
    fontWeight: 850,
    fontSize: 15,
    padding: '9px 4px',
    textShadow: '0 2px 18px rgba(0,0,0,0.45)',
  },

  heroNavPrimary: {
    color: IVORY,
    textDecoration: 'none',
    fontWeight: 900,
    fontSize: 15,
    padding: '10px 20px',
    borderRadius: 999,
    border: '1px solid rgba(253,252,249,0.78)',
    background: 'rgba(253,252,249,0.05)',
    boxShadow: '0 18px 38px rgba(0,0,0,0.24)',
  },

  heroMenuButton: {
    display: 'none',
    width: 42,
    height: 42,
    placeItems: 'center',
    gap: 4,
    borderRadius: 14,
    border: '1px solid rgba(201,164,76,0.76)',
    background: 'rgba(10,26,51,0.78)',
    boxShadow: '0 14px 30px rgba(0,0,0,0.34), inset 0 1px 0 rgba(253,252,249,0.10)',
    cursor: 'pointer',
    padding: 0,
  },

  heroMobileMenu: {
    display: 'none',
    position: 'absolute',
    top: 62,
    right: 12,
    width: 'min(220px, calc(100vw - 24px))',
    padding: 8,
    borderRadius: 16,
    border: '1px solid rgba(201,164,76,0.45)',
    background: 'rgba(10,26,51,0.94)',
    boxShadow: '0 20px 48px rgba(0,0,0,0.38)',
    backdropFilter: 'blur(12px)',
    gap: 6,
  },

  heroMobileMenuLink: {
    color: IVORY,
    textDecoration: 'none',
    fontWeight: 900,
    padding: '11px 12px',
    borderRadius: 12,
    border: '1px solid rgba(253,252,249,0.08)',
    background: 'rgba(253,252,249,0.05)',
  },

  heroMobileMenuPrimary: {
    color: NAVY,
    textDecoration: 'none',
    fontWeight: 900,
    padding: '11px 12px',
    borderRadius: 12,
    border: `1px solid ${GOLD}`,
    background: GOLD,
  },

  heroCenter: {
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: 920,
    margin: '0 auto',
    textAlign: 'center',
    display: 'grid',
    justifyItems: 'center',
  },

  kicker: {
    display: 'inline-block',
    padding: '6px 10px',
    borderRadius: 999,
    border: '1px solid rgba(201,164,76,0.30)',
    background: 'rgba(253,252,249,0.06)',
    color: IVORY,
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },

  h1: {
    margin: '18px 0 18px',
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: 'clamp(48px, 7.2vw, 96px)',
    lineHeight: 0.98,
    maxWidth: 980,
  },
  lead: {
    margin: '0',
    color: 'rgba(253,252,249,0.80)',
    fontSize: 'clamp(17px, 2vw, 23px)',
    lineHeight: 1.6,
    maxWidth: 760,
  },

  ctaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
    marginTop: 10,
  },

  primaryBtn: {
    background: GOLD,
    color: NAVY,
    border: 'none',
    padding: '12px 16px',
    borderRadius: 12,
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 18px 40px rgba(10,26,51,0.35)',
  },

  heroCtaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    flexWrap: 'wrap',
    marginTop: 30,
  },

  heroPrimaryBtn: {
    background: GOLD,
    color: NAVY,
    border: '1px solid rgba(201,164,76,0.86)',
    padding: '16px 24px',
    borderRadius: 16,
    fontWeight: 900,
    cursor: 'pointer',
    boxShadow: '0 20px 48px rgba(0,0,0,0.32), inset 0 1px 0 rgba(253,252,249,0.26)',
  },

  heroSecondaryLink: {
    color: 'rgba(253,252,249,0.76)',
    textDecoration: 'none',
    fontWeight: 900,
    padding: '15px 22px',
    borderRadius: 16,
    border: '1px solid rgba(253,252,249,0.16)',
    background: 'rgba(253,252,249,0.06)',
  },

  secondaryLink: {
    color: 'rgba(253,252,249,0.86)',
    textDecoration: 'none',
    fontWeight: 800,
  },

  note: {
    marginTop: 10,
    color: 'rgba(253,252,249,0.62)',
    fontSize: 13,
  },

  heroCard: {
    position: 'relative',
    overflow: 'hidden',
    background: IVORY,
    color: NAVY,
    border: '1px solid rgba(201,164,76,0.48)',
    borderRadius: 20,
    padding: 12,
    boxShadow: '0 26px 70px rgba(10,26,51,0.35)',
  },
  heroCardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 10,
  },
  badge: {
    width: 38,
    height: 38,
    borderRadius: 12,
    padding: 3,
    background: 'rgba(201,164,76,0.16)',
    border: '1px solid rgba(201,164,76,0.30)',
  },
  badgeText: {
    display: 'block',
    fontWeight: 900,
    color: NAVY,
  },
  proofStatus: {
    marginTop: 2,
    color: '#0F6E56',
    fontSize: 12,
    fontWeight: 900,
  },
  proofPhoto: {
    width: '100%',
    aspectRatio: '4 / 3',
    objectFit: 'cover',
    borderRadius: 14,
    border: '1px solid rgba(201,164,76,0.28)',
  },
  proofContent: {
    padding: '14px 4px 4px',
  },
  proofEyebrow: {
    color: '#8A6D2E',
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  proofTitle: {
    margin: '4px 0 2px',
    color: NAVY,
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: 29,
    lineHeight: 1.05,
  },
  proofMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    color: '#4B5563',
    fontSize: 12,
    fontWeight: 800,
  },
  proofStory: {
    margin: '12px 0',
    color: '#374151',
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: 18,
    lineHeight: 1.45,
  },
  proofLedger: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 8,
    padding: 10,
    borderRadius: 12,
    border: '1px solid rgba(15,110,86,0.24)',
    background: 'rgba(15,110,86,0.07)',
  },
  proofLabel: {
    display: 'block',
    color: '#4B5563',
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
  proofValue: {
    display: 'block',
    marginTop: 3,
    color: NAVY,
    fontSize: 11,
    overflowWrap: 'anywhere',
  },
  heroCardBody: {
    borderRadius: 14,
    padding: 12,
    background: 'rgba(10,26,51,0.55)',
    border: '1px solid rgba(253,252,249,0.08)',
    position: 'relative',
    zIndex: 1,
  },
  lineStrong: {
    height: 12,
    borderRadius: 8,
    background: 'rgba(253,252,249,0.18)',
    marginBottom: 10,
  },
  line: {
    height: 10,
    borderRadius: 8,
    background: 'rgba(253,252,249,0.12)',
    marginBottom: 8,
  },
  lineShort: {
    height: 10,
    width: '55%',
    borderRadius: 8,
    background: 'rgba(253,252,249,0.12)',
    marginBottom: 14,
  },
  pinRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 12,
    opacity: 0.9,
  },
  pinDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    background: GOLD,
    boxShadow: '0 0 0 4px rgba(201,164,76,0.10)',
  },
  heroCardFoot: {
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
    color: 'rgba(253,252,249,0.72)',
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: 0.4,
  },
  footLabel: {},
  footSep: { opacity: 0.6 },

  visualSection: {
    padding: '34px 0 26px',
  },

  visualGrid: {
    display: 'grid',
    gridTemplateColumns: '0.85fr 1.15fr',
    gap: 18,
    alignItems: 'center',
  },

  visualCopy: {
    background: 'rgba(253,252,249,0.05)',
    border: '1px solid rgba(201,164,76,0.16)',
    borderRadius: 18,
    padding: 16,
  },

  section: {
    padding: '26px 0',
  },
  sectionAlt: {
    padding: '26px 0',
    background: 'rgba(253,252,249,0.03)',
    borderTop: '1px solid rgba(201,164,76,0.16)',
    borderBottom: '1px solid rgba(201,164,76,0.16)',
  },

  h2: {
    margin: 0,
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: 26,
  },
  p: {
    margin: '10px 0 0',
    color: 'rgba(253,252,249,0.78)',
    lineHeight: 1.7,
    maxWidth: 760,
  },

  why: {
    background: 'rgba(253,252,249,0.05)',
    border: '1px solid rgba(201,164,76,0.16)',
    borderRadius: 18,
    padding: 16,
  },

  cards: {
    marginTop: 14,
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  },
  card: {
    background: IVORY,
    color: NAVY,
    borderRadius: 18,
    border: '1px solid rgba(201,164,76,0.35)',
    padding: 14,
    boxShadow: '0 18px 52px rgba(10,26,51,0.28)',
  },
  cardTitle: {
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontWeight: 900,
    fontSize: 18,
    marginBottom: 8,
  },
  cardNumber: {
    color: '#8A6D2E',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  q: { fontWeight: 900, marginBottom: 6 },
  a: { color: '#4B5563', lineHeight: 1.6 },

  centerCta: {
    marginTop: 16,
    display: 'flex',
    justifyContent: 'center',
  },

  trustBox: {
    background: 'rgba(253,252,249,0.05)',
    border: '1px solid rgba(201,164,76,0.16)',
    borderRadius: 18,
    padding: 16,
  },
  assetHeading: {
    margin: '14px 0 0',
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: 'clamp(28px, 4vw, 42px)',
    lineHeight: 1.08,
  },
  trustGrid: {
    marginTop: 12,
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 10,
  },
  trustItem: {
    background: 'rgba(10,26,51,0.45)',
    border: '1px solid rgba(253,252,249,0.10)',
    borderRadius: 14,
    padding: 12,
  },
  trustTitle: { fontWeight: 900, marginBottom: 6 },
  trustText: { color: 'rgba(253,252,249,0.72)', lineHeight: 1.6 },
  assetStatement: {
    margin: '18px auto 0',
    maxWidth: 760,
    color: 'rgba(253,252,249,0.90)',
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: 'clamp(20px, 2.5vw, 27px)',
    lineHeight: 1.35,
    textAlign: 'center',
  },

  futureSection: {
    padding: '34px 0',
    background: 'linear-gradient(180deg, rgba(11,16,32,0.82), rgba(10,26,51,0.98))',
    borderTop: '1px solid rgba(214,183,106,0.24)',
    borderBottom: '1px solid rgba(214,183,106,0.24)',
  },
  futureBox: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 20,
    border: '1px solid rgba(214,183,106,0.30)',
    background:
      'radial-gradient(circle at 85% 15%, rgba(214,183,106,0.13), transparent 30%), rgba(5,7,18,0.48)',
    padding: 'clamp(22px, 4vw, 42px)',
    boxShadow: '0 24px 70px rgba(0,0,0,0.24)',
  },
  futureKicker: {
    color: '#D6B76A',
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
  },
  futureHeading: {
    margin: '12px 0 0',
    maxWidth: 900,
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: 'clamp(30px, 5vw, 52px)',
    lineHeight: 1.04,
  },
  futureCopy: {
    display: 'grid',
    gap: 10,
    maxWidth: 820,
    marginTop: 18,
    color: 'rgba(253,252,249,0.78)',
    lineHeight: 1.7,
  },
  futureStatement: {
    margin: '24px 0 0',
    maxWidth: 820,
    color: '#F6EFE3',
    fontFamily: '"Cormorant Garamond", Georgia, serif',
    fontSize: 'clamp(22px, 3vw, 30px)',
    fontWeight: 700,
    lineHeight: 1.3,
  },

  final: {
    padding: '30px 0 44px',
  },
  finalBox: {
    borderRadius: 18,
    border: '1px solid rgba(201,164,76,0.22)',
    background: 'rgba(253,252,249,0.06)',
    padding: 18,
  },
};

export default LandingPage;
// ==========================
// End Version 10: pages/LandingPage.js
// ==========================
